import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';

import { Button, Dialog, DialogActions, DialogContent, Grid, Icon, IconButton, LinearProgress, TextField, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';

import { makeStyles } from '@mui/styles';
import { DataGrid, GridColumns, GridValueGetterParams } from '@mui/x-data-grid';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { PeopleService } from '../../../Component/Api/People/peopleService';
import AppAccordion from '../../../Component/Common/Components/Accordion/AppAccordion';
import { AppDatepicker } from '../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { MDBox } from '../../../Component/Common/Components/MDBox';
import { MDButton } from '../../../Component/Common/Components/MDButton';
import { SkeletonLoading } from '../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { BEE123SalariesEditRole, UserAdminRole } from '../../../Component/Common/Constants/UserRoles';
import { SalaryDtos } from '../../../Component/Model/Dto/Salaries';
import { DateFormatter, defaultObject } from '../../../Component/Model/helpers';
import { decimalFormatter } from '../../../Helpers/DecimalFormatter';
import { userRolesState } from '../../../State/user.Atom';
import { CustomDialogTitle } from '../../Common/Custom/CustomDialogTitle';

interface IPeopleSalariesModalProps {
    id: number;
}

const useStyles = makeStyles({
    root: {
        "& .row--0": {
            backgroundColor: "#bdd7ee" //actual
        },
        "& .row--1": {
            backgroundColor: "white" //input
        },
        "& .row--2": {
            backgroundColor: "#e7e6e6" //percentage
        },
        "& .row--3": {
            backgroundColor: "#d6dce4" //headcountValue
        }
    }
});

export const PeopleSalariesModal = (props: IPeopleSalariesModalProps) => {
    const classes = useStyles();
    const { control: personController, reset } = useForm({
        mode: 'all'
    });
    const { id } = props;

    const editRoleAccess = [
        BEE123SalariesEditRole,
        UserAdminRole
    ];
    const userRoles = useRecoilValue(userRolesState);
    const isEditable = userRoles.some(r => editRoleAccess.includes(r));
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [addSalariesDialogOpen, setAddSalariesDialogOpen] = useState<boolean>(false);
    const [yearToAdd, setYearToAdd] = useState<number>();
    const [salaryData, setSalaryData] = useState<SalaryDtos.SalariesPerYearResponse[]>([]);

    const GetPersonData = async (id: number) => {
        try {
            setIsLoading(true);
            const result = await PeopleService.GetPeople(id);
            reset(result.data);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const GetSalaries = async (id: number) => {
        try {
            setIsLoading(true);
            const result = await PeopleService.GetPeopleSalariesForYears(id);
            setSalaryData(result.data);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const hasSalaryData = (): boolean => {
        return salaryData && salaryData.length > 0;
    }


    useEffect(() => {
        GetPersonData(id);
        GetSalaries(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);



    const handleDeleteYear = async (year: number) => {
        try {
            setIsLoading(true);
            await PeopleService.DeletePeopleSalariesForYear(id, year)
                .then(async (result) => {
                    if (result.status >= 200 && result.status <= 299) {
                        await GetSalaries(id);
                    }
                });
        } catch (error: any) {
            toast.error(error.response.data);
        } finally {
            setIsLoading(false);
        }
    }

    const processSalaryUpdate = async (newRow: SalaryDtos.SalaryResponse, oldRow: SalaryDtos.SalaryResponse) => {
        if (_.isEqual(newRow, oldRow))
            return newRow;

        try {
            let data = salaryData;
            let year = new Date(newRow.salaryDate).getFullYear();
            let idxBlock = data.findIndex(x => x.year === year);
            let block = data[idxBlock];
            if (!block)
                return oldRow;

            let idxSalary = _.findIndex(block.salaries, x => x.salaryDate === newRow.salaryDate);
            let salaries = block.salaries
            let salary = { ...salaries[idxSalary], ctc: newRow.ctc, variable: newRow.variable, fixed: newRow.fixed } || defaultObject;
            salaries = salaries.filter(x => x.salaryDate !== newRow.salaryDate);
            salaries.push(salary);
            salaries = _.sortBy(salaries, x => new Date(x.salaryDate));
            block.salaries = salaries;
            data.splice(idxBlock, 1, block);
            setSalaryData(data);

            return newRow;

        } catch (error: any) {
            toast.error(error.response.data);
            return oldRow;
        } finally { }
    }

    const addYear = async () => {
        try {
            setIsLoading(true);
            if (salaryData.findIndex(x => x.year === yearToAdd) > -1) {
                toast.warning(`Year ${yearToAdd} rows already exists.`);
                return;
            }

            await PeopleService.AddPeopleSalariesForYear(id, yearToAdd!)
                .then(async (result) => {
                    setSalaryData([...salaryData, result.data]);
                });
        } catch (error: any) {
            toast.error(error.response.data);
        } finally {
            setIsLoading(false);
            setAddSalariesDialogOpen(false);
        }
    }

    const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setYearToAdd(+event.target.value);
    };

    const saveSalariesForYear = async (year: number) => {
        const data: SalaryDtos.SalariesPerYearResponse | undefined = _.find(salaryData, x => x.year === year);

        if (!data)
            return;

        try {
            setIsLoading(true);
            await PeopleService.SavePeopleSalariesForYear(props.id, data);
        } catch (error: any) {
            toast.error(error.response.data);
        } finally {
            setIsLoading(false);
        }
    }

    const renderSalaries = () => {
        return hasSalaryData() ? (salaryData.sort((a, b) => b.year - a.year).map((blockItem: SalaryDtos.SalariesPerYearResponse, index) => {
            return (
                <AppAccordion
                    expandOnDefault={true}
                    label={blockItem.year.toString()}
                    summaryActions={isEditable ? renderDeleteYear(blockItem.year) : undefined}
                    key={`acc-year-${blockItem.year}`}>
                    {
                        renderSalariesForYear(blockItem.year, blockItem.salaries)
                    }
                </AppAccordion>
            )
        })) : (<></>);
    }

    const colMonthWidth: number = 200;
    const colSalariesWidth: number = 150;
    const columns: GridColumns = [
        {
            field: 'salaryDate', headerName: 'Month', editable: false, width: colMonthWidth, type: 'string', sortable: false,
            valueGetter: (params: GridValueGetterParams) => {
                return DateFormatter((params.row as SalaryDtos.SalaryResponse).salaryDate.toString(), 'MMMM');
            }
        },
        {
            field: 'ctc', headerName: 'CTC', editable: isEditable, width: colSalariesWidth, type: 'number', sortable: false,
            valueFormatter: ({ value }) => decimalFormatter(value)
        },
        {
            field: 'fixed', headerName: 'FIXED', editable: isEditable, width: colSalariesWidth, type: 'number', sortable: false,
            valueFormatter: ({ value }) => decimalFormatter(value)
        },
        {
            field: 'variable', headerName: 'VARIABLE', editable: isEditable, width: colSalariesWidth, type: 'number', sortable: false,
            valueFormatter: ({ value }) => decimalFormatter(value)
        },

    ];
    const renderSalariesForYear = (year: number, salaries: SalaryDtos.SalaryResponse[]) => {
        return salaries && salaries.length > 0 ?
            (<div>
                <Grid container mb={1}>
                    <Grid item xs={12}>
                        <DataGrid
                            key={`salaries-grd-${year}`}
                            headerHeight={70}
                            className={classes.root}
                            disableSelectionOnClick
                            autoHeight
                            hideFooter
                            disableColumnFilter
                            disableColumnMenu
                            disableColumnSelector
                            rowHeight={25}
                            rows={salaries}
                            columns={columns}
                            editMode="row"
                            isCellEditable={(params) => (isEditable)}
                            experimentalFeatures={{ newEditingApi: true }}
                            processRowUpdate={processSalaryUpdate}
                            components={{
                                LoadingOverlay: LinearProgress,
                            }}
                            loading={isLoading}
                        />
                    </Grid>
                </Grid>
                {isEditable &&
                    <MDButton color="primary" onClick={() => {
                        saveSalariesForYear(year);
                    }}>Save
                    </MDButton>
                }
            </div>)
            :
            (<div>no salary rows for year</div>);
    }

    const renderDeleteYear = (year: number) => {
        return (
            <Tooltip title="Delete salary data">
                <IconButton
                    aria-label="close" size="small"
                    style={{ float: 'right', color: 'inherit' }}
                    onClick={() => handleDeleteYear(year)}>
                    <Icon sx={{ color: '#fff' }}>delete</Icon>
                </IconButton>
            </Tooltip>)
    }

    return (isLoading ?
        <SkeletonLoading formLoading FieldAmount={6} /> :
        <>
            <AppAccordion expandOnDefault={true} label={'Person Details'}>
                <MDBox mt={1}>
                    <form>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput control={personController} name="identificationNumber" label="ID Number" disabled={true} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput control={personController} name="employeeCode" label="Employee Code" disabled={true} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput control={personController} name="firstName" label="Name" disabled={true} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput control={personController} name="lastName" label="Surname" disabled={true} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput control={personController} name="gender" label="Gender" disabled={true} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput control={personController} name="race" label="Race" disabled={true} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppDatepicker control={personController} name="dateOfBirth" label="Date Of Birth" inputFormat='yyyy/MM/dd' disabled={true} />
                            </Grid>
                        </Grid>
                    </form>
                </MDBox>
            </AppAccordion>

            <Box mt={1} mb={1}>
                <Typography variant='h6' component='div' mb={1}>Salaries per year</Typography>
                {/* look at using mdtypography */}
                {isEditable &&
                    <MDButton variant="contained" color="secondary" onClick={() => {
                        setAddSalariesDialogOpen(true);
                    }}>
                        Add Year
                    </MDButton>
                }
            </Box>

            <Box>
                <Dialog open={addSalariesDialogOpen} maxWidth='sm'>
                    <CustomDialogTitle id="customized-dialog-title" onClose={() => setAddSalariesDialogOpen(false)}>
                        Add salaries for year
                    </CustomDialogTitle>
                    <DialogContent >
                        <Box mt={1}>
                            <TextField
                                id="txt-yearToAdd-withValue"
                                label="Year"
                                sx={{width:'300px'}}
                                onChange={handleYearChange}
                                value={yearToAdd}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Box>
                            <Button variant="contained" onClick={() => {
                                addYear();
                            }}>Add</Button>
                        </Box>
                    </DialogActions>
                </Dialog>

                {/* add delete confirm dialog - showconfirmationDialog*/}


                {renderSalaries()}
            </Box>
        </>
    )
}

